import { cs } from "../utils/currency";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Search,
  X,
  Phone,
  Award,
  Plus,
  ChevronDown,
  RotateCcw,
  ShoppingBag,
  Wallet,
} from "lucide-react";
import { CompensationPayerToggle, CompensationPayer } from "./OrderActionModals";
import api, { ApiResponse } from "../services/api";
import { toast } from "react-hot-toast";

export interface LoyaltyLevel {
  name: string;
  color: string;
  discount: number;
}

export interface LoyaltyCustomer {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  points: number;
  createdAt: string;
  spendAmount: number;
  loyaltyLevel?: LoyaltyLevel | null;
  bonusWallet: { balance: number; pendingBalance: number; currency: string };
  // Main (cash) wallet
  wallet?: { balance: number };
  blackListForCash: boolean;
  blackListForCard: boolean;
}

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface CompensationEntry {
  points: number;
  reason: string;
  paidBy?: "restaurant" | "admin";
  restaurantName?: string;
  restaurantAddress?: string;
  createdAt: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  timestamp: string;
  orderStatus?: string;
  choice?: string;
  restaurantName?: string;
  compensationHistory?: CompensationEntry[];
  /** Also true when the compensation itself is hidden (platform-paid, admin-only) */
  alreadyCompensated?: boolean;
  // Loyalty this order earned and how it was paid (from the server)
  loyaltyEarned?: number;
  loyaltyState?: "credited" | "pending" | "none";
  paymentMethods?: string[];
  loyaltyReversible?: boolean;
}

const PAYMENT_LABELS: Record<string, string> = {
  stripe: "Card",
  square: "Card",
  card: "Card",
  wallet: "Wallet",
  bonus_wallet: "Bonus Wallet",
  cash: "Cash",
};

/** (compensation ÷ order total) × loyalty earned — same formula as the server. */
const loyaltyToRemove = (compensation: number, order: Order) => {
  const earned = order.loyaltyEarned ?? 0;
  if (!order.loyaltyReversible || !(compensation > 0) || !(order.totalAmount > 0) || earned <= 0) return 0;
  return Math.min(earned, Math.round((compensation / order.totalAmount) * earned * 100) / 100);
};

export const LoyaltyLevelBadge: React.FC<{ level?: LoyaltyLevel | null }> = ({ level }) => {
  if (!level) {
    return <span className="text-xs text-gray-400">No level</span>;
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold border"
      style={{ color: level.color, borderColor: level.color, backgroundColor: `${level.color}1A` }}
    >
      {level.name} · {level.discount}%
    </span>
  );
};

/**
 * Customer details modal: order history (with search + infinite scroll),
 * manual points and per-order compensation. Shared by the User List and Ledger.
 * `readOnly` hides the points/compensation actions (used for location admins).
 */
const UserDetailsModal: React.FC<{
  user: LoyaltyCustomer;
  onClose: () => void;
  onBalanceChange?: (userId: string, newBalance: number) => void;
  readOnly?: boolean;
}> = ({ user, onClose, onBalanceChange, readOnly = false }) => {
  const [selectedUser, setSelectedUser] = useState<LoyaltyCustomer | null>(user);
  const [userOrders, setUserOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  // Refs that don't trigger re-renders
  const ordersSearchRef = useRef(""); // always tracks latest committed search
  const ordersDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null); // cancel in-flight requests
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Add manual points modal (inside user orders modal)
  const [showAddPointsModal, setShowAddPointsModal] = useState(false);
  const [manualPoints, setManualPoints] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [manualLoading, setManualLoading] = useState(false);

  // Inline compensation accordion
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [compensationPoints, setCompensationPoints] = useState("");
  const [compensationReason, setCompensationReason] = useState("");
  const [compensationPaidBy, setCompensationPaidBy] = useState<CompensationPayer>("restaurant");
  const [compensationLoading, setCompensationLoading] = useState(false);

  /**
   * Core fetch — always cancels the previous in-flight request via AbortController.
   * Only updates state if the response belongs to the latest request (not a stale one).
   */
  const fetchOrders = useCallback(async (
    phoneNumber: string,
    search: string,
    page: number,
    append: boolean,
  ) => {
    // Cancel any previous in-flight request
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (page === 1) setOrdersLoading(true);
    else setOrdersLoadingMore(true);

    try {
      const params = new URLSearchParams({ page: String(page) });
      if (search) params.set('search', search);
      const res = await api.get<ApiResponse<Order[]> & { total: number; page: number; hasMore: boolean }>(
        `/loyalty/orders/${phoneNumber}?${params}`,
        { signal: controller.signal },
      );
      // Guard: ignore if this controller was already replaced (another request started)
      if (abortControllerRef.current !== controller) return;
      if (res.data.success) {
        setUserOrders(prev => append ? [...prev, ...res.data.data] : res.data.data);
        setOrdersHasMore(res.data.hasMore);
        setOrdersPage(page);
      }
    } catch (err: any) {
      // Silently ignore aborted requests — not an error
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      toast.error("Failed to load orders.");
    } finally {
      // Only clear loading spinners for the request that is still "current"
      if (abortControllerRef.current === controller) {
        setOrdersLoading(false);
        setOrdersLoadingMore(false);
      }
    }
  }, []);

  // Load the first page whenever a different user is opened
  useEffect(() => {
    if (ordersDebounceRef.current) clearTimeout(ordersDebounceRef.current);
    setSelectedUser(user);
    setUserOrders([]);
    setOrderSearch("");
    ordersSearchRef.current = "";
    setOrdersPage(1);
    setOrdersHasMore(false);
    setExpandedOrderId(null);
    setCompensationPoints("");
    setCompensationReason("");
    fetchOrders(user.phoneNumber, "", 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user._id]);

  const closeUserOrdersModal = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (ordersDebounceRef.current) clearTimeout(ordersDebounceRef.current);
    onClose();
  };

  /**
   * Debounced + deduplicated search.
   * - Debounce: 400ms — waits until user stops typing before firing
   * - Deduplication: skips the fetch if the term hasn't changed since the last fired request
   */
  const handleOrderSearch = (value: string) => {
    setOrderSearch(value);
    ordersSearchRef.current = value;
    if (ordersDebounceRef.current) clearTimeout(ordersDebounceRef.current);
    ordersDebounceRef.current = setTimeout(() => {
      const term = ordersSearchRef.current; // read latest value at fire time
      if (selectedUser) {
        setUserOrders([]);
        setOrdersPage(1);
        fetchOrders(selectedUser.phoneNumber, term, 1, false);
      }
    }, 400);
  };

  // Infinite scroll — IntersectionObserver watches a sentinel div at the bottom of the list.
  // Only fires when not already loading and there are more pages.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !ordersHasMore || ordersLoading || ordersLoadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && selectedUser && !ordersLoadingMore && !ordersLoading) {
          fetchOrders(selectedUser.phoneNumber, ordersSearchRef.current, ordersPage + 1, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [ordersHasMore, ordersLoading, ordersLoadingMore, ordersPage, selectedUser, fetchOrders]);

  // Manual add points
  const handleManualPoints = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const pts = parseFloat(manualPoints);
    if (isNaN(pts) || pts <= 0) {
      toast.error("Enter a valid points amount.");
      return;
    }
    setManualLoading(true);
    try {
      await api.post("/loyalty/manual-points", {
        phoneNumber: selectedUser.phoneNumber,
        points: pts,
        reason: manualReason,
        restaurantId: process.env.REACT_APP_RESTAURANT_ID,
      });
      toast.success("Points added successfully!");
      setShowAddPointsModal(false);
      setManualPoints("");
      setManualReason("");
      // Update modal and card in-place immediately
      const newBalance = parseFloat(((selectedUser.bonusWallet?.balance ?? 0) + pts).toFixed(2));
      const updatedUser = { ...selectedUser, bonusWallet: { ...selectedUser.bonusWallet, balance: newBalance } };
      setSelectedUser(updatedUser);
      onBalanceChange?.(selectedUser._id, newBalance);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add points.");
    } finally {
      setManualLoading(false);
    }
  };

  // Toggle accordion row; reset form when switching to a different order
  const toggleOrderExpand = (orderId: string) => {
    setExpandedOrderId(prev => {
      if (prev === orderId) return null;
      setCompensationPoints("");
      setCompensationReason("");
      return orderId;
    });
  };

  // Submit inline compensation
  const handleCompensation = async (e: React.FormEvent, order: Order) => {
    e.preventDefault();
    if (!selectedUser) return;
    const pts = parseFloat(compensationPoints);
    if (isNaN(pts) || pts <= 0) {
      toast.error("Enter a valid points amount.");
      return;
    }
    if (pts > order.totalAmount) {
      toast.error(`Points cannot exceed order total ($${order.totalAmount.toFixed(2)}).`);
      return;
    }
    setCompensationLoading(true);
    try {
      const res = await api.post("/loyalty/order-refund", {
        phoneNumber: selectedUser.phoneNumber,
        orderId: order._id,
        points: pts,
        reason: compensationReason,
        paidBy: compensationPaidBy,
        restaurantId: process.env.REACT_APP_RESTAURANT_ID,
      });
      const loyaltyRemoved = Number(res.data?.loyaltyRemoved) || 0;
      toast.success(
        loyaltyRemoved > 0
          ? `Compensation added. ${loyaltyRemoved.toFixed(2)} loyalty points from this order removed.`
          : "Compensation points added!",
      );
      setCompensationPoints("");
      setCompensationReason("");
      const newEntry: CompensationEntry = {
        points: pts,
        reason: compensationReason,
        paidBy: compensationPaidBy,
        createdAt: new Date().toISOString(),
      };
      setCompensationPaidBy("restaurant");
      setUserOrders(prev =>
        prev.map(o => o._id === order._id
          ? {
              ...o,
              compensationHistory: [...(o.compensationHistory ?? []), newEntry],
              loyaltyEarned: Math.max(0, (o.loyaltyEarned ?? 0) - loyaltyRemoved),
            }
          : o)
      );
      // Credited loyalty comes out of the balance; pending loyalty was never in it
      const fromBalance = order.loyaltyState === "credited" ? loyaltyRemoved : 0;
      const newBalance = parseFloat(((selectedUser.bonusWallet?.balance ?? 0) + pts - fromBalance).toFixed(2));
      setSelectedUser(u => u ? { ...u, bonusWallet: { ...u.bonusWallet, balance: newBalance } } : u);
      onBalanceChange?.(selectedUser._id, newBalance);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to process compensation.");
    } finally {
      setCompensationLoading(false);
    }
  };

  return (
    <>
    {selectedUser && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ top: "0", left: "0", right: "0", bottom: "0" }}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={closeUserOrdersModal}
        />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-auto">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex-shrink-0">
            <div className="flex justify-between items-center mb-3">
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-0">
                  {selectedUser.name || "Unknown Customer"}
                </h2>
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                  <Phone size={12} />
                  {selectedUser.phoneNumber}
                  <span className="ml-2 flex items-center gap-1">
                    <Award size={12} className="text-yellow-500" />
                    <span className="font-medium text-blue-600">{Number(selectedUser.bonusWallet?.balance ?? 0).toFixed(2)} pts</span>
                  </span>
                  <span className="ml-2 flex items-center gap-1" title="Customer wallet balance">
                    <Wallet size={12} className="text-teal-500" />
                    <span className="font-medium text-teal-700">
                      Wallet ${Number(selectedUser.wallet?.balance ?? 0).toFixed(2)}
                    </span>
                  </span>
                  {selectedUser.loyaltyLevel && (
                    <span className="ml-2">
                      <LoyaltyLevelBadge level={selectedUser.loyaltyLevel} />
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!readOnly && (
                <button
                  onClick={() => setShowAddPointsModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition"
                >
                  <Plus size={14} />
                   <span className="sm:hidden">Add</span>
                   <span className="hidden sm:inline">Add Points</span>
                </button>
                )}
                <button
                  type="button"
                  onClick={closeUserOrdersModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="text"
                value={orderSearch}
                onChange={(e) => handleOrderSearch(e.target.value)}
                placeholder="Search by order number..."
                className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
              {orderSearch && (
                <button
                  onClick={() => handleOrderSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Orders list */}
          <div className="flex-1 overflow-y-auto">
            {ordersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : userOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                {orderSearch ? (
                  <>
                    <Search size={40} className="mb-3 opacity-40" />
                    <p className="font-medium">No orders match "{orderSearch}"</p>
                  </>
                ) : (
                  <>
                    <ShoppingBag size={40} className="mb-3 opacity-40" />
                    <p className="font-medium">No orders found</p>
                    <p className="text-sm mt-1">This customer has no orders yet.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {userOrders.map((order) => {
                  const isExpanded = expandedOrderId === order._id;
                  const history = order.compensationHistory ?? [];
                  const totalCompensated = history.reduce((sum, c) => sum + c.points, 0);
                  const compensationPointsNum = parseFloat(compensationPoints);
                  const exceedsMax = isExpanded && !isNaN(compensationPointsNum) && compensationPointsNum > order.totalAmount;

                  return (
                    <div
                      key={order._id}
                      className={`border rounded-lg overflow-hidden transition-colors ${
                        isExpanded ? "border-blue-300 shadow-sm" : "border-gray-200"
                      }`}
                    >
                      {/* Row header — click anywhere to toggle */}
                      <button
                        type="button"
                        onClick={() => toggleOrderExpand(order._id)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-800 text-sm">#{order.orderNumber}</span>
                              {order.orderStatus && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  order.orderStatus === "completed" || order.orderStatus === "delivered" || order.orderStatus === "pickedup"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}>
                                  {order.orderStatus}
                                </span>
                              )}
                              {history.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                                  Compensated
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {new Date(order.timestamp).toLocaleString()} · {order.items.length} item{order.items.length !== 1 ? "s" : ""}{order.choice ? ` · ${order.choice}` : ""}
                            </p>
                            {order.restaurantName && (
                              <p className="text-xs text-gray-500 font-medium mt-0.5">{order.restaurantName}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                          <span className="font-bold text-gray-800">{cs()}{order.totalAmount.toFixed(2)}</span>
                          <ChevronDown
                            size={18}
                            className={`text-gray-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>

                      {/* Accordion body */}
                      <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${isExpanded ? "max-h-[1200px]" : "max-h-0"}`}>
                        <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4 space-y-4">

                          {/* Order items */}
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Order Items</p>
                            <div className="space-y-1">
                              {order.items.map((item: OrderItem, i: number) => (
                                <div key={i} className="flex justify-between text-sm">
                                  <span className="text-gray-700">{item.quantity}× {item.name}</span>
                                  <span className="text-gray-600 font-medium">{cs()}{(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between font-bold text-gray-800 text-sm">
                              <span>Order Total</span>
                              <span>{cs()}{order.totalAmount.toFixed(2)}</span>
                            </div>
                          </div>

                          {/* Compensation history */}
                          {history.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Previous Compensations</p>
                              <div className="space-y-2">
                                {history.map((entry, idx) => (
                                  <div key={idx} className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-gray-700 font-medium">{entry.reason}</p>
                                      {entry.restaurantName && (
                                        <p className="text-xs text-gray-500">{entry.restaurantName}{entry.restaurantAddress ? ` · ${entry.restaurantAddress}` : ""}</p>
                                      )}
                                      <p className="text-xs text-gray-400">
                                        {new Date(entry.createdAt).toLocaleDateString()} · Paid by{" "}
                                        <span className="font-medium text-gray-600">
                                          {entry.paidBy === "admin" ? "Admin" : "Restaurant"}
                                        </span>
                                      </p>
                                    </div>
                                    <span className="text-sm font-semibold text-purple-700 whitespace-nowrap">{cs()}{entry.points.toFixed(2)}</span>
                                  </div>
                                ))}
                                {history.length > 1 && (
                                  <div className="pt-2 border-t border-gray-200 flex justify-between">
                                    <span className="text-sm font-semibold text-gray-600">Total compensated</span>
                                    <span className="text-sm font-semibold text-purple-700">{cs()}{totalCompensated.toFixed(2)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Compensation form — only one compensation per order */}
                          {readOnly ? null : order.alreadyCompensated || history.length > 0 ? (
                            <p className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700">
                              This order has already been compensated.
                            </p>
                          ) : (
                          <form onSubmit={(e) => handleCompensation(e, order)}>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Add Compensation</p>
                            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                              <span>
                                Paid by:{" "}
                                <span className="font-medium text-gray-800">
                                  {(order.paymentMethods ?? []).map((m) => PAYMENT_LABELS[m] || m).join(" + ") || "—"}
                                </span>
                              </span>
                              <span>
                                Loyalty earned on this order:{" "}
                                <span className="font-medium text-gray-800">
                                  {(order.loyaltyEarned ?? 0).toFixed(2)} pts
                                  {order.loyaltyState === "pending" ? " (pending)" : ""}
                                </span>
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <label className="block mb-1.5 text-xs font-medium text-gray-700">
                                  Points <span className="text-gray-400 font-normal">(max ${order.totalAmount.toFixed(2)})</span>
                                </label>
                                <input
                                  type="number"
                                  value={compensationPoints}
                                  onChange={(e) => setCompensationPoints(e.target.value)}
                                  placeholder="0.00"
                                  required
                                  step="0.01"
                                  min="0.01"
                                  max={order.totalAmount}
                                  className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                                    exceedsMax ? "border-red-400 focus:ring-red-400 bg-red-50" : "border-gray-300 focus:ring-blue-500"
                                  }`}
                                />
                                {exceedsMax && <p className="mt-1 text-xs text-red-600">Cannot exceed ${order.totalAmount.toFixed(2)}</p>}
                              </div>
                              <div>
                                <label className="block mb-1.5 text-xs font-medium text-gray-700">Reason *</label>
                                <input
                                  type="text"
                                  value={compensationReason}
                                  onChange={(e) => setCompensationReason(e.target.value)}
                                  placeholder="e.g. Wrong order..."
                                  required
                                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />
                              </div>
                            </div>
                            <div className="mb-3">
                              <CompensationPayerToggle value={compensationPaidBy} onChange={setCompensationPaidBy} />
                            </div>
                            {(() => {
                              const removeAmt = loyaltyToRemove(compensationPointsNum, order);
                              if ((order.loyaltyEarned ?? 0) <= 0) return null;
                              if (!order.loyaltyReversible) {
                                return (
                                  <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                                    Paid in cash — loyalty points from this order are kept.
                                  </p>
                                );
                              }
                              return (
                                <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                  Loyalty to remove: <span className="font-semibold">{removeAmt.toFixed(2)} pts</span>{" "}
                                  <span className="text-amber-700">
                                    = ({isNaN(compensationPointsNum) ? "0.00" : compensationPointsNum.toFixed(2)} ÷{" "}
                                    {order.totalAmount.toFixed(2)}) × {(order.loyaltyEarned ?? 0).toFixed(2)}
                                  </span>
                                </p>
                              );
                            })()}
                            <button
                              type="submit"
                              disabled={compensationLoading || exceedsMax}
                              className="w-full flex items-center justify-center gap-2 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 transition disabled:opacity-60"
                            >
                              <RotateCcw size={14} />
                              {compensationLoading ? "Processing..." : "Add Compensation"}
                            </button>
                          </form>
                          )}

                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="h-2" />
                {ordersLoadingMore && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                  </div>
                )}
                {!ordersHasMore && userOrders.length > 0 && (
                  <p className="text-center text-xs text-gray-400 py-3">All orders loaded</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* ================= ADD POINTS MODAL ================= */}
    {showAddPointsModal && selectedUser && !readOnly && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{ top: "0", left: "0", right: "0", bottom: "0" }}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={() => setShowAddPointsModal(false)}
        />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
          <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
            <h2 className="text-xl font-bold text-gray-800 mb-0">Add Points</h2>
            <button
              type="button"
              onClick={() => setShowAddPointsModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <form id="manual-points-form" onSubmit={handleManualPoints} className="p-6">
              <div className="space-y-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Phone size={18} className="text-blue-600" />
                    <span className="font-semibold text-gray-800">{selectedUser.phoneNumber}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Award size={18} className="text-yellow-500" />
                    <span className="text-lg font-bold text-blue-600">
                      Current: {Number(selectedUser.bonusWallet?.balance ?? 0).toFixed(2)} points
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Points to Add *
                  </label>
                  <input
                    type="number"
                    value={manualPoints}
                    onChange={(e) => setManualPoints(e.target.value)}
                    placeholder="e.g. 50"
                    required
                    step="0.01"
                    min="0.01"
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={manualReason}
                    onChange={(e) => setManualReason(e.target.value)}
                    placeholder="e.g. Compensation, promotion..."
                    required
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </form>
          </div>
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowAddPointsModal(false)}
              className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="manual-points-form"
              disabled={manualLoading}
              className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-60"
            >
              {manualLoading ? "Saving..." : "Add Points"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default UserDetailsModal;
